import React, { ReactNode } from 'react';
import styles from './DataTableV2.module.scss';

export type ColumnTypeV2<T> = {
  header: string;
  accessor: string;
  render?: (record: T) => ReactNode;
  width?: string | number;
};

export interface DataTableV2Props<T> {
  data: T[];
  columns: ColumnTypeV2<T>[];
  loading?: boolean;
  emptyComponent?: ReactNode;
  onRowClick?: (record: T, index: number) => void;
  className?: string;
}

const getNestedValue = (obj: any, path: string) => {
  return path.split('.').reduce((prev, curr) => {
    return prev && prev[curr] !== undefined ? prev[curr] : undefined;
  }, obj);
};

function DataTableV2<T extends { id?: string | number }>({
  data,
  columns,
  loading = false,
  emptyComponent,
  onRowClick,
  className = '',
}: Readonly<DataTableV2Props<T>>) {
  const renderCell = (item: T, column: ColumnTypeV2<T>) => {
    if (column.render) {
      return column.render(item);
    }

    const value = getNestedValue(item, column.accessor);

    if (value === null || value === undefined || value === '') {
      return 'N/A';
    }

    if (typeof value === 'object') {
      return JSON.stringify(value);
    }

    return String(value);
  };

  return (
    <div className={`${styles.tableWrapper} ${className}`}>
      <table className={styles.table}>
        <thead>
          <tr>
            {columns.map((column, index) => (
              <th
                key={`${index}-${column.header}`}
                style={{ width: column.width }}
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {loading && (
            <tr>
              <td colSpan={columns.length} className={styles.stateCell}>
                Loading...
              </td>
            </tr>
          )}

          {!loading && data.length === 0 && (
            <tr>
              <td colSpan={columns.length} className={styles.stateCell}>
                {emptyComponent || 'No data available'}
              </td>
            </tr>
          )}

          {!loading && data.length > 0 && data.map((record, index) => (
            <tr
              key={record.id ?? index}
              onClick={onRowClick ? () => onRowClick(record, index) : undefined}
              className={onRowClick ? styles.clickable : ''}
            >
              {columns.map((column) => (
                <td key={`${record.id ?? index}-${column.header}`} style={{ width: column.width, maxWidth: column.width }}>
                  {renderCell(record, column)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default DataTableV2;
